import { create } from 'zustand';

export type BillingDialog =
  | 'subscriptionWizard'
  | 'resumeSubscription'
  | 'addSubAccount'
  | 'enableTeamBilling'
  | 'upgradeRequired'
  | 'addonRequired'
  | 'addonUpdate'
  | 'unpaidInvoices'
  | 'billingInformation';

interface BillingDialogState {
  activeDialog: BillingDialog | null;
  dialogContext: Record<string, unknown>;
  openDialog: (dialog: BillingDialog, context?: Record<string, unknown>) => void;
  closeDialog: () => void;
  /**
   * SP53 — ScreenManagement is an OVERLAY dialog (not part of activeDialog enum) so it can
   * stack on top of the subscription wizard. scio1 parity: clicking Select Free with too
   * many screens opens screenManagementModal WITHOUT closing the underlying Update Subscription
   * Plan wizard, letting the user remove screens and immediately retry.
   */
  screenManagementOpen: boolean;
  screenManagementContext: Record<string, unknown>;
  openScreenManagement: (context?: Record<string, unknown>) => void;
  closeScreenManagement: () => void;
  /**
   * Downgrade-to-Free confirm is an OVERLAY (not part of activeDialog enum) so it stacks on
   * top of the subscription wizard. Cancelling the confirm leaves the wizard intact instead
   * of dropping the user back to the SubscriptionPage.
   */
  downgradeOpen: boolean;
  downgradeContext: Record<string, unknown>;
  openDowngrade: (context?: Record<string, unknown>) => void;
  closeDowngrade: () => void;
}

export const useBillingDialogStore = create<BillingDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},
  openDialog: (dialog, context = {}) => set({ activeDialog: dialog, dialogContext: context }),
  closeDialog: () => set({ activeDialog: null, dialogContext: {} }),
  screenManagementOpen: false,
  screenManagementContext: {},
  openScreenManagement: (context = {}) =>
    set({ screenManagementOpen: true, screenManagementContext: context }),
  closeScreenManagement: () => set({ screenManagementOpen: false, screenManagementContext: {} }),
  downgradeOpen: false,
  downgradeContext: {},
  openDowngrade: (context = {}) => set({ downgradeOpen: true, downgradeContext: context }),
  closeDowngrade: () => set({ downgradeOpen: false, downgradeContext: {} }),
}));

/** Open the subscription wizard in upgrade mode — skips to plan step, filters plans >= minPlan */
export function openUpgradeWizard(minPlanRequired: string) {
  useBillingDialogStore.getState().openDialog('subscriptionWizard', {
    preSelectedPlan: minPlanRequired,
    minPlanRequired,
  });
}
